import { FC, useCallback, useContext, useMemo } from "react";
import { useRouter } from "next/router";
import { stringifyUrl } from "query-string";
import Link from "next/link";
import { isArray, some } from "lodash-es";
import { Box } from "@otrium/core";
import { MultiselectHitType } from "src/types/Algolia";
import { formatName } from "src/utils/formatName";
import { VirtualRefinementList } from "src/organisms/AlgoliaProductList/VirtualInstantSearch";
import { CATEGORIES_FACET } from "src/utils/algolia";
import { getSelectedNodes } from "src/organisms/AlgoliaProductList/utils/getSelectedNodes";
import { ItemText, StyledFilters } from "./CategoriesTree.styled";
import NavigationContext from "src/contexts/navigation.context";
import { PLPType } from "src/types/PLPType.d";
import { useSegment } from "src/hooks/useSegment";

interface Props {
  departmentSlug?: string;
  genderId: string;
  categoriesTree?: MultiselectHitType[];
  isNewPlp: boolean;
  plpType?: PLPType;
}

const CategoriesTree: FC<Props> = ({
  departmentSlug,
  genderId,
  categoriesTree,
  isNewPlp,
  plpType,
}) => {
  const router = useRouter();
  const { trackingCategoryItemClicked } = useSegment();
  const { navigationItems } = useContext(NavigationContext);
  const additionalCategories = useMemo(
    () =>
      navigationItems?.navigation_links
        .filter((link) => !link.show_on_top)
        .slice(0, 4) || [],
    [navigationItems?.navigation_links]
  );
  const isNewItemsPage = router.asPath.includes("new");
  const { orderBy, brandOrCategoriesSlug, category } = router.query;
  const slug = brandOrCategoriesSlug || category;

  const currentSlug = slug && isArray(slug) ? slug[slug.length - 1] : slug;

  const href = departmentSlug
    ? `/[genderId]/${departmentSlug}/[slug]`
    : isNewItemsPage
    ? stringifyUrl({
        url: "/[genderId]/new/[slug]",
        query: { orderBy },
      })
    : `/[genderId]/[...slug]`;

  const getRoute = (slug: string) =>
    departmentSlug
      ? `/${genderId}/${departmentSlug}/${slug}`
      : isNewItemsPage
      ? stringifyUrl({
          url: `/${genderId}/new/${slug}`,
          query: { orderBy },
        })
      : `/${genderId}/${slug}`;

  const tree = useMemo(() => {
    if (categoriesTree && categoriesTree.length > 0) {
      const treeWithoutRoot = categoriesTree[0].items;
      const isNewInRoot = !slug && isNewPlp;
      if (!currentSlug && (departmentSlug || isNewInRoot)) {
        return treeWithoutRoot;
      }
      if (currentSlug === genderId) {
        return treeWithoutRoot;
      }
      const hasSelected = some(
        treeWithoutRoot,
        (item) => item.label?.split("||")[2] === currentSlug
      );
      if (hasSelected) {
        return treeWithoutRoot;
      } else {
        return getSelectedNodes(treeWithoutRoot, currentSlug as string);
      }
    }
    return undefined;
  }, [categoriesTree, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryItemClick = useCallback(
    (slug: string, name: string) => {
      void trackingCategoryItemClicked(slug, name);
    },
    [trackingCategoryItemClicked]
  );

  return (
    <StyledFilters>
      <VirtualRefinementList attribute={CATEGORIES_FACET} limit={1000} />
      <Box as="ul">
        <>
          {plpType === PLPType.CATEGORY_PLP &&
            additionalCategories.map(({ link, title, colour }, index) => (
              <Box
                as="li"
                key={index}
                data-testid="products-category-tree-item"
                mb={index === additionalCategories.length - 1 ? 5 : 2}
              >
                <Link href={link} passHref legacyBehavior>
                  <Box as="a">
                    <ItemText
                      as="span"
                      active={currentSlug === slug}
                      color={colour}
                    >
                      {formatName(title)}
                    </ItemText>
                  </Box>
                </Link>
              </Box>
            ))}
          {tree?.map((c, index) => {
            const splittedItem = c.label?.split("||");
            const name = splittedItem[1] || "";
            const slug = splittedItem[2] || "";
            return (
              <Box
                as="li"
                key={c.label}
                data-testid="products-category-tree-item"
                mb={2}
              >
                <Link href={href} as={getRoute(slug)} passHref legacyBehavior>
                  <Box
                    as="a"
                    onClick={() => {
                      handleCategoryItemClick(slug, name);
                    }}
                  >
                    <ItemText active={currentSlug === slug} color="Black">
                      {formatName(name)}
                    </ItemText>
                  </Box>
                </Link>
                {c.items &&
                  (!currentSlug ||
                    currentSlug === slug ||
                    currentSlug === genderId) && (
                    <Box
                      mt={2}
                      key={`cat-${index}-current`}
                      as="ul"
                      pl={[4, 4, 4, 4, 5]}
                    >
                      {c.items?.map((subCategory, i) => {
                        const splittedSubCategory =
                          subCategory.label?.split("||");
                        const subCategoryName = splittedSubCategory[1] || "";
                        const subCategorySlug = splittedSubCategory[2] || "";
                        return (
                          <Box as="li" key={`children-cat-${i}`} mb={2}>
                            <Link
                              passHref
                              href={href}
                              as={getRoute(subCategorySlug)}
                              legacyBehavior
                            >
                              <Box
                                as="a"
                                onClick={() => {
                                  handleCategoryItemClick(
                                    subCategorySlug,
                                    subCategoryName
                                  );
                                }}
                              >
                                <ItemText color="Black">
                                  {formatName(subCategoryName)}
                                </ItemText>
                              </Box>
                            </Link>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
              </Box>
            );
          })}
        </>
      </Box>
    </StyledFilters>
  );
};

export default CategoriesTree;
